<template>
  <div class="tw-relative tw-size-full">
    <canvas :id="chartId"></canvas>
  </div>
</template>

<script lang="ts" setup>
import {
  Chart,
  ChartData,
  ChartOptions,
  ChartType,
  Filler,
  Legend,
  Tooltip,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  withDefaults,
  defineProps,
  onMounted,
  ref,
  watch,
  onUnmounted,
  nextTick,
} from 'vue';
import { debounce } from 'quasar';
import { useRoot } from '@/utils/component';

interface Props {
  type: ChartType;
  data: ChartData;
  disableDataLabels?: boolean;
  options?: ChartOptions;
}
const props = withDefaults(defineProps<Props>(), {
  disableDataLabels: false,
  option: () => ({}),
});

if (!props.disableDataLabels) {
  Chart.register(ChartDataLabels);
  (
    Chart.defaults.plugins as unknown as Record<
      string,
      { anchor?: string; align?: string }
    >
  ).datalabels.anchor = 'end';
  (
    Chart.defaults.plugins as unknown as Record<
      string,
      { anchor?: string; align?: string }
    >
  ).datalabels.align = 'end';
}
Chart.register(Filler, Legend, Tooltip);
const root = useRoot();
const chartId = `chart-${Math.random().toString(36).slice(2)}`;
let chart = ref<Chart | undefined>();
const renderChart = async () => {
  await nextTick(); // important to make sure the canvas is rendered
  if (chart.value) chart.value.destroy();
  const { type, data, options } = props;
  const canvas = document.getElementById(chartId) as HTMLCanvasElement | null;
  if (!canvas) return;
  const ctx = canvas?.getContext('2d');
  if (!ctx) return;
  chart.value = new Chart(ctx, {
    type,
    data,
    options: {
      maintainAspectRatio: false,
      ...options,
      plugins: {
        legend: {
          display: false,
        },
        ...(options?.plugins || {}),
      },
    },
  });
};
// const updateChart = () => {
//   const { data, options } = props;
//   if (chart) {
//     chart.options = {
//       maintainAspectRatio: false,
//       ...options,
//     };
//     chart.data = data;
//     chart.update();
//   }
// };
onMounted(() => {
  renderChart();
});
onUnmounted(() => {
  chart.value?.destroy();
});
watch(
  () => props.data,
  () => {
    renderChart();
  },
  { deep: true }
);

watch(
  () => root.$q.screen.width,
  () => {
    debounce(renderChart)();
  }
);
</script>
